import React from 'react';

interface StepWelcomeProps {
  onNext: () => void;
}

const StepWelcome: React.FC<StepWelcomeProps> = ({ onNext }) => {
  return (
    <div className="flex flex-col">
      <h1 className="text-4xl font-bold text-white leading-tight">
        Finally see your whole financial picture — personal and business.
      </h1>
      <p className="text-base text-white/40 mt-3 mb-8">Takes about 3 minutes.</p>

      {/* Mock dashboard preview */}
      <div className="rounded-xl border border-purple-500/20 bg-white/5 p-5 mb-8 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-600/10 rounded-full blur-2xl pointer-events-none" />

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-purple-400 text-sm">✦</span>
            <p className="text-xs text-purple-300 font-medium">Your dashboard, unlocked</p>
          </div>
          {/* Static account filter toggle — matches real dashboard */}
          <div className="flex rounded-lg bg-white/5 p-0.5 gap-0.5 select-none pointer-events-none">
            {['Personal', 'Business', 'All'].map((tab) => (
              <span
                key={tab}
                className={`text-xs px-2 py-0.5 rounded-md ${
                  tab === 'All'
                    ? 'bg-white/15 text-white font-medium'
                    : 'text-white/30'
                }`}
              >
                {tab}
              </span>
            ))}
          </div>
        </div>

        {/* Net worth hero number */}
        <div className="mb-3">
          <p className="text-xs text-white/40 mb-0.5">Net Worth</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-white">$487,500</span>
            <span className="text-xs text-emerald-400 font-medium">▲ +12.4% this year</span>
          </div>
        </div>

        {/* Area chart — mirrors the real indigo ComposedChart */}
        <div className="rounded-lg overflow-hidden mb-3">
          <svg viewBox="0 0 280 64" className="w-full h-16" preserveAspectRatio="none">
            <defs>
              <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(99,102,241)" stopOpacity="0.25" />
                <stop offset="100%" stopColor="rgb(99,102,241)" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {/* area fill */}
            <path
              d="M0,54 C30,56 40,60 70,46 C90,36 105,44 130,42 C155,40 160,26 185,22 C205,18 230,12 280,4 L280,64 L0,64 Z"
              fill="url(#nwGrad)"
            />
            {/* assets dashed line */}
            <path
              d="M0,50 C30,52 40,56 70,40 C90,28 105,38 130,36 C155,34 160,20 185,16 C205,12 230,8 280,2"
              fill="none"
              stroke="rgba(110,231,183,0.6)"
              strokeWidth="1.5"
              strokeDasharray="4 3"
            />
            {/* net worth line */}
            <path
              d="M0,54 C30,56 40,60 70,46 C90,36 105,44 130,42 C155,40 160,26 185,22 C205,18 230,12 280,4"
              fill="none"
              stroke="rgb(99,102,241)"
              strokeWidth="2"
            />
          </svg>
        </div>

        {/* Stat row */}
        <div className="flex gap-2">
          {[
            { label: 'Assets', value: '$612k', trend: '+8%', up: true },
            { label: 'Liabilities', value: '$124k', trend: '-3%', up: false },
            { label: 'Debt Ratio', value: '20%', trend: null },
          ].map(({ label, value, trend, up }) => (
            <div key={label} className="flex-1 rounded-lg bg-white/5 px-3 py-2 text-center">
              <p className="text-xs text-white/40 mb-0.5">{label}</p>
              <p className="text-sm font-semibold text-white">{value}</p>
              {trend && (
                <p className={`text-xs ${up ? 'text-emerald-400' : 'text-rose-400'}`}>{trend}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Benefit bullets */}
      <ul className="mb-6 space-y-1">
        {[
          'See your complete net worth — personal & business',
          'Track income, expenses & savings rate',
          'Built specifically for Canadian owner-operators',
        ].map((text) => (
          <li key={text} className="text-base text-white/60 py-1">
            ✦ {text}
          </li>
        ))}
      </ul>

      <p className="text-sm text-white/30 italic mb-8">
        Built by Canadian business owners who couldn't find a single tool that tracked personal and corporate finances together.
      </p>

      <button
        type="button"
        onClick={onNext}
        className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors self-start"
      >
        Let's get started →
      </button>
    </div>
  );
};

export default StepWelcome;
